import { useState } from 'react';
import useReCaptchaV3 from '~/client/components/Auth/Shared/utils/useReCaptchaV3';
import useCid from '~/client/hooks/cookies/marketing/useCid';
import useRefAff from '~/client/hooks/cookies/marketing/useRefAff';
import useRefCode from '~/client/hooks/cookies/marketing/useRefCode';
import useFriendRef from '~/client/hooks/cookies/useFriendRef';
import useLanguage from '~/client/hooks/useLanguage';
import useSite from '~/client/hooks/useSite';
import internationalCallingCodes from '~/client/shared/form/FormSelect/InternationalCallingCodes';
import { useRegisterMutation } from './graphql/RegisterMutation.generated';
import useHandleRegisterResponse from './useHandleRegisterResponse';
import type FormValues from '../FormValues';

const useHandleRegister = ({
  isCaptchaRequired,
}: {
  isCaptchaRequired: boolean;
}) => {
  const { site } = useSite();
  const { code: language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [refAff] = useRefAff();
  const [friendRef] = useFriendRef();
  const [cid] = useCid();
  const refCode = useRefCode();
  const [registerUser] = useRegisterMutation();

  const { getV3Token, captchaVersion } = useReCaptchaV3({ isRegister: true });
  const { handleRegisterResponse, loading: registerResponseLoading } =
    useHandleRegisterResponse();

  type Args = Omit<FormValues, 'dateOfBirth'> & { dateOfBirth: number };

  const handleRegister = async (values: Args) => {
    setLoading(true);

    const {
      username,
      email,
      password,
      offers,
      dateOfBirth,
      offersCall,
      offersSms,
      captcha,
    } = values;
    const isRecaptchaV3 = captchaVersion === 'v3' && !isCaptchaRequired;
    const version = isRecaptchaV3 ? 'v3' : 'v2';
    const captchaToken = isRecaptchaV3 ? await getV3Token() : captcha;
    const { phone, countryCode = 'XX' } = values;
    const countryCallingCode =
      internationalCallingCodes?.[countryCode]?.code?.toString();
    const { data } = await registerUser({
      variables: {
        input: {
          site,
          username,
          email,
          dateOfBirth,
          password,
          offers,
          offersCall,
          offersSms,
          captcha: captchaToken,
          captchaVersion: version,
          cid,
          refAff,
          friendRef,
          refCode,
          language,
          phone:
            countryCallingCode && phone
              ? `+${countryCallingCode}${phone}`
              : phone,
        },
      },
    });
    setLoading(false);
    return handleRegisterResponse(data?.userManagementRegisterAsUser);
  };

  return {
    loading: loading || registerResponseLoading,
    handleRegister,
    handleRegisterResponse,
  };
};

export default useHandleRegister;
